import { MemberApi, OrderAPI, PayApi } from '@api/index'
import { appMobx, memberMobx } from '@mobxStore'
import { getPlatform, miniAppRouteTo } from '@utils'
import { uuid } from '@dt/fe-utils'

const WECHAT_CHANNELS = ['WechatPayWeb', 'ShengPayWechatWeb', 'ShengPayWechatWebV2']
const ALIPAY_CHANNELS = ['AlipayApp', 'ShengPayAlipayWeb', 'ShengPayAlipayWebV2']
const ACTIVITY_USE_TYPE = 'activity'
const PAYMENT_CHANNEL_USE_TYPE = 'goods'

type TActivityPayResult = 'success' | 'async'
const ACTIVITY_PAY_CANCELLED = 'ACTIVITY_PAY_CANCELLED'

interface IActivityPayParams {
  ordersId: string;
  amount: number;
  storeId: number;
  activityId: number | string;
}

const createActivityPayCancelError = () => Object.assign(new Error(i18n.t('mkt.activityReserve.paymentCancelled')), {
  code: ACTIVITY_PAY_CANCELLED,
})

export const isActivityPayCancelError = (error: any) => error?.code === ACTIVITY_PAY_CANCELLED

export const isActivityPaymentEnvironment = () => {
  const { isWeixinMiniProgram, isWeixinBrower, isAlipay } = getPlatform()
  return isWeixinMiniProgram || isWeixinBrower || isAlipay
}

const getThirdAuthInfo = () => {
  let thirdAuthInfo: any = {}
  try {
    thirdAuthInfo = JSON.parse(memberMobx.thirdAuthInfo || '{}') || {}
  } catch (e) {
    thirdAuthInfo = {}
  }
  return thirdAuthInfo
}

const getGlobalOpenId = async () => {
  const thirdAuthInfo = getThirdAuthInfo()
  const localOpenId = thirdAuthInfo.gOpenId || memberMobx.memberInfo?.gOpenId
  if (localOpenId) return localOpenId

  const memberId = memberMobx.memberInfo?.memberId
  if (!memberId) return undefined

  // 本地授权信息缺失时与点餐支付保持一致，从会员渠道信息补齐并回写缓存。
  const memberData: any = await MemberApi.channelDetail({ memberId })
  if (memberData?.error || !memberData?.gOpenId) return undefined

  memberMobx.setThirdAuthInfo({
    ...thirdAuthInfo,
    gOpenId: memberData.gOpenId,
    openId: memberData.openId || thirdAuthInfo.openId,
  })
  return memberData.gOpenId
}

const getWechatOpenId = async () => {
  const { isWeixinMiniProgram } = getPlatform()

  const openIdData = isWeixinMiniProgram
    ? { openId: window.Config.openId, appId: window.Config.appId }
    : { openId: await getGlobalOpenId() }

  return openIdData
}

const getAlipayBuyerId = () => getGlobalOpenId()

const getActivityPayChannel = async (storeId: number, channelTypes: string[]) => {
  const res: any = await PayApi.listPaymentTypesStoreV2({
    storeId,
    useTypes: [PAYMENT_CHANNEL_USE_TYPE],
  })
  if (res?.errors) return undefined
  const channels = res?.result?.[PAYMENT_CHANNEL_USE_TYPE] || []
  return channels.find((item: any) => channelTypes.includes(item?.channelType))?.channelType
}

const invokeWechatPay = (channelType: string, result: any, ordersId: string, activityId: number | string): Promise<TActivityPayResult> => {
  const wapPayRequest = result?.wapPayRequest || {}
  const resultOrdersId = result?.ordersId || ordersId
  const { isWeixinMiniProgram, isWeixinBrower } = getPlatform()

  if (['ShengPayWechatWeb', 'ShengPayWechatWebV2'].includes(channelType)) {
    wapPayRequest.package = wapPayRequest.packageStr || wapPayRequest.package
  }

  if (isWeixinMiniProgram) {
    miniAppRouteTo({
      url: `/pages/pay/index?p=${encodeURIComponent(JSON.stringify(wapPayRequest))}&o=${resultOrdersId}&scene=activityReserve&aid=${activityId}`
    })
    return Promise.resolve('async')
  }

  if (isWeixinBrower) {
    return new Promise((resolve, reject) => {
      window.WeixinJSBridge.invoke('getBrandWCPayRequest', wapPayRequest, (res: any) => {
        if (res.err_msg === 'get_brand_wcpay_request:ok') {
          resolve('success')
        } else {
          reject(createActivityPayCancelError())
        }
      })
    })
  }

  return Promise.reject(new Error(i18n.t('mkt.activityReserve.wechatOnly')))
}

const payZeroActivityOrder = async (params: IActivityPayParams, prepayCreate: any): Promise<TActivityPayResult> => {
  const clientNumber = prepayCreate.clientNumber || appMobx.deviceId || 'mobile'
  const { error, result } = (await PayApi.batchPay({
    brandId: Config.BRANDID,
    bo,
    prepayCreateId: prepayCreate.prepayCreateId,
    clientNumber,
    orderSource: 'dt',
    useType: ACTIVITY_USE_TYPE,
    storeId: prepayCreate.storeId || params.storeId,
    currency: 'USD',
    opAppId: Config.appId || '',
    requestId: uuid(),
    ordersId: params.ordersId,
    channels: [{
      requestId: uuid(),
      channelType: 'ZeroPay',
      amount: 0,
      tip: 0,
      ext: {}
    }],
    ext: {
      deviceType: 'mobile',
      deviceId: clientNumber,
    },
  })) as any

  if (error || result?.error) throw error || result
  return 'success'
}

const payWechatActivityOrder = async (params: IActivityPayParams, prepayCreate: any): Promise<TActivityPayResult> => {
  const channelType = await getActivityPayChannel(params.storeId, WECHAT_CHANNELS)
  if (!channelType) throw new Error(i18n.t('mkt.activityReserve.wechatNotConfigured'))

  const openIdData = await getWechatOpenId()
  if (!openIdData?.openId) throw new Error(i18n.t('mkt.activityReserve.wechatInfoMissing'))

  const clientNumber = prepayCreate.clientNumber || appMobx.deviceId || 'mobile'
  const { error, result } = (await PayApi.batchPay({
    brandId: Config.BRANDID,
    bo,
    prepayCreateId: prepayCreate.prepayCreateId,
    clientNumber,
    orderSource: 'dt',
    useType: ACTIVITY_USE_TYPE,
    storeId: prepayCreate.storeId || params.storeId,
    currency: 'USD',
    opAppId: Config.appId || '',
    requestId: uuid(),
    ordersId: params.ordersId,
    channels: [{
      requestId: uuid(),
      channelType,
      amount: params.amount,
      tip: 0,
      ext: openIdData,
    }],
    ext: {
      deviceType: 'mobile',
      deviceId: appMobx.deviceId || clientNumber,
    },
  })) as any

  if (error || result?.error) throw error || result
  return invokeWechatPay(channelType, result, params.ordersId, params.activityId)
}

const invokeAlipayPay = (result: any, ordersId: string): Promise<TActivityPayResult> => {
  const wapPayRequest = result?.wapPayRequest || {}
  const tradeNo = wapPayRequest.tradeNo || wapPayRequest.tradeNO
  const { isAliPayMiniProgram } = getPlatform()

  if (!tradeNo) {
    return Promise.reject(new Error(wapPayRequest.subMsg || i18n.t('mkt.activityReserve.alipayLaunchFailed')))
  }

  if (isAliPayMiniProgram) {
    if (window.my?.tradePay) {
      return new Promise((resolve, reject) => {
        window.my.tradePay({ tradeNO: tradeNo }, (payResult: any) => {
          if (payResult?.resultCode === '9000') {
            resolve('success')
          } else {
            reject(createActivityPayCancelError())
          }
        })
      })
    }

    miniAppRouteTo({
      url: `/pages/pay/index?tradeNO=${tradeNo}&backurl=${encodeURIComponent(`/result/${ordersId}`)}`
    })
    return Promise.resolve('async')
  }

  return new Promise((resolve, reject) => {
    const invoke = () => {
      window.AlipayJSBridge.call('tradePay', { tradeNO: tradeNo }, (payResult: any) => {
        if (payResult?.resultCode === '9000') {
          resolve('success')
        } else {
          reject(createActivityPayCancelError())
        }
      })
    }

    if (window.AlipayJSBridge) {
      invoke()
    } else {
      document.addEventListener('AlipayJSBridgeReady', invoke, { once: true })
    }
  })
}

const payAlipayActivityOrder = async (params: IActivityPayParams, prepayCreate: any): Promise<TActivityPayResult> => {
  const channelType = await getActivityPayChannel(params.storeId, ALIPAY_CHANNELS)
  if (!channelType) throw new Error(i18n.t('mkt.activityReserve.alipayNotConfigured'))

  const buyerId = await getAlipayBuyerId()
  if (!buyerId) throw new Error(i18n.t('mkt.activityReserve.alipayInfoMissing'))

  const clientNumber = prepayCreate.clientNumber || appMobx.deviceId || 'mobile'
  const { error, result } = (await PayApi.batchPay({
    brandId: Config.BRANDID,
    bo,
    prepayCreateId: prepayCreate.prepayCreateId,
    clientNumber,
    orderSource: 'dt',
    useType: ACTIVITY_USE_TYPE,
    storeId: prepayCreate.storeId || params.storeId,
    currency: 'USD',
    opAppId: Config.appId || '',
    requestId: uuid(),
    ordersId: params.ordersId,
    channels: [{
      requestId: uuid(),
      channelType,
      amount: params.amount,
      tip: 0,
      ext: { buyerId },
    }],
    ext: {
      deviceType: 'mobile',
      deviceId: appMobx.deviceId || clientNumber,
    },
  })) as any

  if (error || result?.error) throw error || result
  return invokeAlipayPay(result, params.ordersId)
}

export const payActivityReserveOrder = async (params: IActivityPayParams): Promise<TActivityPayResult> => {
  if (params.amount > 0 && !isActivityPaymentEnvironment()) {
    throw new Error(i18n.t('mkt.activityReserve.completePaymentInApp'))
  }

  const clientNumber = appMobx.deviceId || 'mobile'
  const startPayRes: any = await OrderAPI.startPay({
    ordersId: params.ordersId,
    paidFee: params.amount,
    clientNumber,
    storeId: params.storeId,
  })
  if (startPayRes?.errors) throw startPayRes.errors

  const prepayCreate = startPayRes?.result
  if (!prepayCreate?.prepayCreateId) throw new Error(i18n.t('mkt.activityReserve.paymentFailed'))

  if (params.amount === 0) return payZeroActivityOrder(params, prepayCreate)

  const { isAlipay } = getPlatform()
  return isAlipay
    ? payAlipayActivityOrder(params, prepayCreate)
    : payWechatActivityOrder(params, prepayCreate)
}
